import { useId, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface AccordionSectionProps {
  /** Section heading. */
  title: string;
  /** Optional supporting text under the title. */
  description?: string;
  /** Whether the section starts expanded. Defaults to false. */
  defaultOpen?: boolean;
  /** Optional element rendered on the right of the header (e.g. a badge). */
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * A single collapsible accordion section, styled to match Card.
 *
 * Self-contained (manages its own open/closed state) and accessible: the header
 * is a button with aria-expanded / aria-controls wired to the panel.
 */
export function AccordionSection({
  title,
  description,
  defaultOpen = false,
  action,
  className,
  children,
}: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const headerId = useId();

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-white shadow-soft',
        'dark:border-border-dark dark:bg-surface-dark-secondary',
        className,
      )}
    >
      <h3>
        <button
          type="button"
          id={headerId}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'flex w-full items-center justify-between gap-3 rounded-t-xl px-6 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40',
            !open && 'rounded-b-xl',
          )}
        >
          <span className="min-w-0">
            <span className="block text-base font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </span>
            {description && (
              <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">
                {description}
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {action}
            <svg
              className={cn('h-5 w-5 text-gray-400 transition-transform', open && 'rotate-180')}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </span>
        </button>
      </h3>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          className="border-t border-border px-6 py-5 dark:border-border-dark"
        >
          {children}
        </div>
      )}
    </div>
  );
}
